import { useEffect, type FunctionComponent } from "react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, DatePicker, Drawer, Form, Input, InputNumber, Radio, Select, Space, TimePicker, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  habitTaskWindow,
  HabitCycleType,
  HabitTimeType,
  HabitType,
  type HabitChecklistItem,
  type HabitCycle,
  type HabitSchedule,
  type HabitTemplateEntity,
  type HabitTime,
  type HabitWeekday,
} from "@fitness-app/data-models/entities/Habit";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { HABIT_ICON_KEYS, habitIconLabels } from "@fitness-app/data-models/domain/habitIcons";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";
import { HabitIcon } from "./habitIcons";
import {
  allWeekdays,
  asHabitColor,
  DEFAULT_HABIT_COLOR,
  DEFAULT_HABIT_ICON,
  HABIT_COLOR_KEYS,
  HABIT_TIME_FORMAT,
  habitColorHex,
  habitColorLabels,
  habitCycleTypeOptions,
  habitTimeTypeOptions,
  habitTypeOptions,
  weekdayOptions,
  type HabitColorKey,
} from "./constants";

type Props = {
  open: boolean;
  habit: HabitTemplateEntity | null;
  onClose: () => void;
  onSaved: () => void;
};

type ChecklistFormItem = {
  id?: string;
  label: string;
};

type HabitFormValues = {
  name: string;
  description?: string;
  icon: string;
  color: HabitColorKey;
  type: HabitType;
  daysOfWeek: number[];
  // TRE-1656 — dated tasks: a single date or a date range instead of the weekly schedule.
  taskDateMode?: "single" | "range";
  taskDate?: Dayjs;
  taskDateRange?: [Dayjs, Dayjs];
  cycleType: HabitCycleType;
  untilDate?: Dayjs;
  cycleLength?: number;
  totalCycles?: number;
  timeType: HabitTimeType;
  hour?: Dayjs;
  timeRange?: [Dayjs, Dayjs];
  checklist?: ChecklistFormItem[];
  dateFrom?: Dayjs;
};

const buildSchedule = (values: HabitFormValues): HabitSchedule => {
  const weekly: HabitSchedule = {
    daysOfWeek: [...(values.daysOfWeek ?? [])].sort((a, b) => a - b) as HabitWeekday[],
  };

  if (values.type === HabitType.Task) {
    if (values.taskDateMode === "range" && values.taskDateRange) {
      const [start, end] = values.taskDateRange;
      return { daysOfWeek: [], startDate: start.format("YYYY-MM-DD"), dueDate: end.format("YYYY-MM-DD") };
    }
    if (values.taskDate) {
      const date = values.taskDate.format("YYYY-MM-DD");
      return { daysOfWeek: [], startDate: date, dueDate: date };
    }
    // A task with no date picked (one created before dated tasks existed) keeps its weekly
    // schedule — inventing a deadline here would silently drop it. isHabitDueOn falls back
    // to daysOfWeek for exactly this case.
    return weekly;
  }

  return weekly;
};

const buildCycle = (values: HabitFormValues): HabitCycle => {
  switch (values.cycleType) {
    case HabitCycleType.UntilDate:
      return {
        type: HabitCycleType.UntilDate,
        untilDate: (values.untilDate ?? dayjs()).format("YYYY-MM-DD"),
      };
    case HabitCycleType.CycleNumber:
      return {
        type: HabitCycleType.CycleNumber,
        cycleLength: values.cycleLength ?? 1,
        totalCycles: values.totalCycles ?? 1,
      };
    case HabitCycleType.Infinite:
    default:
      return { type: HabitCycleType.Infinite };
  }
};

const buildTime = (values: HabitFormValues): HabitTime => {
  switch (values.timeType) {
    case HabitTimeType.Hour:
      return {
        type: HabitTimeType.Hour,
        hour: (values.hour ?? dayjs()).format(HABIT_TIME_FORMAT),
      };
    case HabitTimeType.Range: {
      const [start, end] = values.timeRange ?? [dayjs(), dayjs()];
      return {
        type: HabitTimeType.Range,
        start: start.format(HABIT_TIME_FORMAT),
        end: end.format(HABIT_TIME_FORMAT),
      };
    }
    case HabitTimeType.AllDay:
    default:
      return { type: HabitTimeType.AllDay };
  }
};

const buildChecklist = (values: HabitFormValues): HabitChecklistItem[] => {
  if (values.type !== HabitType.Group) {
    return [];
  }

  return (values.checklist ?? [])
    .filter((item) => item.label?.trim())
    .map((item) => ({
      id: item.id ?? generateUniqId(),
      label: item.label.trim(),
    }));
};

const toFormValues = (habit: HabitTemplateEntity): HabitFormValues => {
  const cycle = (habit.cycle ?? { type: HabitCycleType.Infinite }) as HabitCycle;
  const time = (habit.time ?? { type: HabitTimeType.AllDay }) as HabitTime;
  const schedule = (habit.schedule ?? { daysOfWeek: allWeekdays }) as HabitSchedule;
  const checklist = (habit.checklist ?? []) as HabitChecklistItem[];
  const taskWindow = habitTaskWindow(schedule);
  const taskDateMode: HabitFormValues["taskDateMode"] =
    taskWindow && taskWindow.start !== taskWindow.end ? "range" : "single";

  return {
    name: habit.name,
    description: habit.description ?? undefined,
    icon: habit.icon || DEFAULT_HABIT_ICON,
    color: asHabitColor(habit.color),
    type: habit.type as HabitType,
    daysOfWeek: schedule.daysOfWeek ?? [],
    taskDateMode,
    taskDate: taskWindow && taskDateMode === "single" ? dayjs(taskWindow.end) : undefined,
    taskDateRange:
      taskWindow && taskDateMode === "range" ? [dayjs(taskWindow.start), dayjs(taskWindow.end)] : undefined,
    cycleType: cycle.type,
    untilDate: cycle.type === HabitCycleType.UntilDate ? dayjs(cycle.untilDate) : undefined,
    cycleLength: cycle.type === HabitCycleType.CycleNumber ? cycle.cycleLength : undefined,
    totalCycles: cycle.type === HabitCycleType.CycleNumber ? cycle.totalCycles : undefined,
    timeType: time.type,
    hour: time.type === HabitTimeType.Hour ? dayjs(time.hour, HABIT_TIME_FORMAT) : undefined,
    timeRange:
      time.type === HabitTimeType.Range
        ? [dayjs(time.start, HABIT_TIME_FORMAT), dayjs(time.end, HABIT_TIME_FORMAT)]
        : undefined,
    checklist: checklist.map((item) => ({ id: item.id, label: item.label })),
    dateFrom: habit.dateFrom ? dayjs(habit.dateFrom) : undefined,
  };
};

const defaultValues: Partial<HabitFormValues> = {
  icon: DEFAULT_HABIT_ICON,
  color: DEFAULT_HABIT_COLOR,
  type: HabitType.Single,
  daysOfWeek: allWeekdays,
  taskDateMode: "single",
  cycleType: HabitCycleType.Infinite,
  timeType: HabitTimeType.AllDay,
  checklist: [],
};

/** Grid of lucide habit icons; works as an antd Form.Item control (value = lucide key). */
const HabitIconPicker: FunctionComponent<{ value?: string; onChange?: (value: string) => void }> = ({
  value,
  onChange,
}) => (
  <div style={{ display: "grid", gap: 6, gridTemplateColumns: "repeat(8, 1fr)" }}>
    {HABIT_ICON_KEYS.map((key) => {
      const selected = value === key;
      return (
        <Button
          key={key}
          type={selected ? "primary" : "default"}
          onClick={() => onChange?.(key)}
          style={{ alignItems: "center", display: "flex", height: 38, justifyContent: "center", padding: 0 }}
          title={habitIconLabels[key]}
          aria-label={habitIconLabels[key]}
        >
          <HabitIcon iconKey={key} size={18} color={selected ? "#fff" : undefined} />
        </Button>
      );
    })}
  </div>
);

const habitColorOptions = HABIT_COLOR_KEYS.map((key) => ({
  value: key,
  label: (
    <Space size={8}>
      <span
        style={{
          backgroundColor: habitColorHex[key],
          borderRadius: "50%",
          display: "inline-block",
          height: 12,
          width: 12,
        }}
      />
      {habitColorLabels[key]}
    </Space>
  ),
}));

export const HabitFormDrawer: FunctionComponent<Props> = ({ open, habit, onClose, onSaved }) => {
  const [form] = Form.useForm<HabitFormValues>();
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const isEdit = Boolean(habit);

  const typeValue = Form.useWatch("type", form);
  const cycleTypeValue = Form.useWatch("cycleType", form);
  const timeTypeValue = Form.useWatch("timeType", form);
  const daysOfWeekValue = Form.useWatch("daysOfWeek", form);
  const taskDateModeValue = Form.useWatch("taskDateMode", form);

  // TRE-1656 — a task is once-and-done under a date (or range): no weekly schedule, no cycle.
  const isTaskType = typeValue === HabitType.Task;
  /** Genitive form for copy like "nazwa nawyku / zadania". */
  const entityWordGenitive = isTaskType ? "zadania" : "nawyku";

  useEffect(() => {
    if (!open) {
      return;
    }

    if (habit) {
      form.setFieldsValue(toFormValues(habit));
    } else {
      form.resetFields();
      form.setFieldsValue(defaultValues);
    }
  }, [open, habit, form]);

  const saveHabitMutation = useMutation({
    mutationFn: async (values: HabitFormValues) => {
      const payload = {
        name: values.name,
        description: values.description ?? null,
        icon: values.icon || DEFAULT_HABIT_ICON,
        color: asHabitColor(values.color),
        type: values.type,
        schedule: buildSchedule(values),
        // Tasks carry their own date window — cycles and dateFrom don't apply.
        cycle: values.type === HabitType.Task ? { type: HabitCycleType.Infinite } : buildCycle(values),
        time: buildTime(values),
        checklist: buildChecklist(values),
        dateFrom: values.type !== HabitType.Task && values.dateFrom ? values.dateFrom.format("YYYY-MM-DD") : null,
      };

      if (habit) {
        const { error } = await supabase
          .from("habit_template")
          .update(payload as never)
          .eq("id", habit.id);

        if (error) {
          throw new Error(error.message);
        }

        return;
      }

      const { error } = await supabase.from("habit_template").insert({
        ...payload,
        createdBy: memberId ?? null,
      } as never);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: (_data, values) => {
      const isTask = values.type === HabitType.Task;
      void message.success(
        isEdit
          ? isTask
            ? "Zadanie zaktualizowane"
            : "Nawyk zaktualizowany"
          : isTask
            ? "Zadanie utworzone"
            : "Nawyk utworzony",
      );
      void queryClient.invalidateQueries({ queryKey: ["habits-list"] });
      form.resetFields();
      onSaved();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zapisać: ${error.message}`);
    },
  });

  const allDaysSelected = (daysOfWeekValue?.length ?? 0) === allWeekdays.length;

  const toggleEveryDay = (checked: boolean) => {
    form.setFieldValue("daysOfWeek", checked ? allWeekdays : []);
  };

  return (
    <Drawer
      title={isEdit ? (isTaskType ? "Edytuj zadanie" : "Edytuj nawyk") : "Nowy nawyk / zadanie"}
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Anuluj</Button>
          <Button type="primary" loading={saveHabitMutation.isLoading} onClick={() => form.submit()}>
            {isEdit ? "Zapisz" : "Utwórz"}
          </Button>
        </Space>
      }
    >
      <Form<HabitFormValues>
        form={form}
        layout="vertical"
        initialValues={defaultValues}
        onFinish={(values) => saveHabitMutation.mutate(values)}
      >
        <Form.Item name="name" label="Nazwa" rules={[{ required: true, message: `Podaj nazwę ${entityWordGenitive}` }]}>
          <Input placeholder={isTaskType ? "np. Wyślij zdjęcia sylwetki 📸" : "np. Wypij 2 litry wody 💧"} />
        </Form.Item>

        <Form.Item name="description" label="Opis">
          <Input.TextArea rows={3} placeholder={`Krótki opis ${entityWordGenitive}`} />
        </Form.Item>

        <Form.Item name="icon" label="Ikona" rules={[{ required: true, message: "Wybierz ikonę" }]}>
          <HabitIconPicker />
        </Form.Item>

        <Form.Item name="color" label="Kolor" rules={[{ required: true, message: "Wybierz kolor" }]}>
          <Select options={habitColorOptions} />
        </Form.Item>

        <Form.Item name="type" label="Typ" rules={[{ required: true, message: "Wybierz typ" }]}>
          <Select options={habitTypeOptions} />
        </Form.Item>

        {isTaskType ? (
          // TRE-1656 — a task gets a concrete date (or date range) instead of a weekly schedule.
          <>
            <Form.Item name="taskDateMode" label="Termin" rules={[{ required: true }]}>
              <Radio.Group
                optionType="button"
                options={[
                  { value: "single", label: "Konkretna data" },
                  { value: "range", label: "Zakres dat" },
                ]}
              />
            </Form.Item>
            {taskDateModeValue === "range" ? (
              <Form.Item
                name="taskDateRange"
                label="Do zrobienia w dniach"
                rules={[{ required: true, message: "Wybierz zakres dat" }]}
              >
                <DatePicker.RangePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
              </Form.Item>
            ) : (
              <Form.Item name="taskDate" label="Data wykonania" rules={[{ required: true, message: "Wybierz datę" }]}>
                <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
              </Form.Item>
            )}
          </>
        ) : (
          <Form.Item label="Dni tygodnia" required>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Button
                size="small"
                type={allDaysSelected ? "primary" : "default"}
                onClick={() => toggleEveryDay(!allDaysSelected)}
              >
                Codziennie
              </Button>
              <Form.Item
                name="daysOfWeek"
                noStyle
                rules={[{ required: true, message: "Wybierz przynajmniej jeden dzień", type: "array", min: 1 }]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Wybierz dni"
                  options={weekdayOptions}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Space>
          </Form.Item>
        )}

        {typeValue === HabitType.Group ? (
          <Form.Item label="Lista kontrolna">
            <Form.List name="checklist">
              {(fields, { add, remove }) => (
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" style={{ width: "100%" }}>
                      <Form.Item
                        {...field}
                        name={[field.name, "label"]}
                        rules={[{ required: true, message: "Podaj treść pozycji" }]}
                        style={{ marginBottom: 0, width: 380 }}
                      >
                        <Input placeholder="Pozycja listy" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ label: "" })} block>
                    Dodaj pozycję
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>
        ) : null}

        {!isTaskType ? (
          <Form.Item name="cycleType" label="Cykl" rules={[{ required: true }]}>
            <Select options={habitCycleTypeOptions} />
          </Form.Item>
        ) : null}

        {!isTaskType && cycleTypeValue === HabitCycleType.UntilDate ? (
          <Form.Item name="untilDate" label="Do dnia" rules={[{ required: true, message: "Wybierz datę" }]}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
        ) : null}

        {!isTaskType && cycleTypeValue === HabitCycleType.CycleNumber ? (
          <Space size={16} style={{ display: "flex" }}>
            <Form.Item
              name="cycleLength"
              label="Długość cyklu (dni)"
              rules={[{ required: true, message: "Podaj długość cyklu" }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="totalCycles"
              label="Liczba cykli"
              rules={[{ required: true, message: "Podaj liczbę cykli" }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Space>
        ) : null}

        <Form.Item name="timeType" label="Pora dnia" rules={[{ required: true }]}>
          <Select options={habitTimeTypeOptions} />
        </Form.Item>

        {timeTypeValue === HabitTimeType.Hour ? (
          <Form.Item name="hour" label="Godzina" rules={[{ required: true, message: "Wybierz godzinę" }]}>
            <TimePicker style={{ width: "100%" }} format={HABIT_TIME_FORMAT} minuteStep={5} />
          </Form.Item>
        ) : null}

        {timeTypeValue === HabitTimeType.Range ? (
          <Form.Item
            name="timeRange"
            label="Przedział godzin"
            rules={[{ required: true, message: "Wybierz przedział" }]}
          >
            <TimePicker.RangePicker style={{ width: "100%" }} format={HABIT_TIME_FORMAT} minuteStep={5} />
          </Form.Item>
        ) : null}

        {!isTaskType ? (
          <Form.Item name="dateFrom" label="Obowiązuje od (opcjonalnie)">
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
        ) : null}
      </Form>
    </Drawer>
  );
};

export default HabitFormDrawer;
